import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTimes, 
  faPaperPlane, 
  faRobot, 
  faUser, 
  faProjectDiagram,
  faCircle,
  faSpinner,
  faPlay,
  faStop,
  faCheck,
  faCog
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../context/AuthContext';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://nsgruve.ai.pocsolution.com';

const WorkflowChatDialog = ({ isOpen, onClose, workflow }) => {
  const { token } = useAuth();
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [wsConnection, setWsConnection] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [executionStatus, setExecutionStatus] = useState('idle'); // idle, running, completed, error
  const [processedMessageIds, setProcessedMessageIds] = useState(new Set()); // Track processed messages to prevent duplicates
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Auto scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Initialize chat session when dialog opens
  useEffect(() => {
    if (isOpen && workflow && token && !sessionId) {
      initializeWorkflowChat();
    }
    
    return () => {
      if (wsConnection) {
        wsConnection.close();
      }
    };
  }, [isOpen, workflow, token]);

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (isOpen) {
      // Reset processed message IDs when starting a new workflow chat
      setProcessedMessageIds(new Set());
    } else {
      // Clean up when dialog closes
      setMessages([]);
      setSessionId(null);
      setExecutionStatus('idle');
      setProcessedMessageIds(new Set());
    }
  }, [isOpen]);

  // Focus input when dialog opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const initializeWorkflowChat = async () => {
    console.log('🎯 FRONTEND DEBUG: Initializing workflow chat', { workflowId: workflow.id, workflowName: workflow.name });
    
    try {
      // Create a new chat session for workflow execution
      const sessionResponse = await fetch(`${API_BASE_URL}/api/chat/sessions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          session_name: `Workflow: ${workflow.name || 'Unnamed'}`,
          workflow_id: String(workflow.id)
        })
      });

      if (sessionResponse.ok) {
        const sessionData = await sessionResponse.json();
        console.log('✅ FRONTEND DEBUG: Session created successfully', sessionData);
        setSessionId(sessionData.session_id);
        connectWebSocket(sessionData.session_id);
        
        // Add welcome message
        setMessages([{
          id: 'welcome',
          type: 'system',
          content: `🚀 Workflow "${workflow.name}" is ready! Send a message to start the workflow execution.`,
          timestamp: new Date().toISOString()
        }]);
        
        setExecutionStatus('ready');
      } else {
        console.error('Failed to create workflow session');
        setMessages([{
          id: 'error',
          type: 'error',
          content: '❌ Failed to initialize workflow chat session',
          timestamp: new Date().toISOString()
        }]);
      }
    } catch (error) {
      console.error('Error initializing workflow chat:', error);
      setMessages([{
        id: 'error',
        type: 'error',
        content: `❌ Error: ${error.message}`,
        timestamp: new Date().toISOString()
      }]);
    }
  };

  const connectWebSocket = (sessionIdToConnect) => {
    if (wsConnection) {
      wsConnection.close();
    }

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${protocol}//${window.location.host}/api/chat/ws/${sessionIdToConnect}?token=${token}`;
    
    const ws = new WebSocket(wsUrl);
    
    ws.onopen = () => {
      console.log('🔌 FRONTEND DEBUG: WebSocket connected', wsUrl);
      setConnectionStatus('connected');
      setWsConnection(ws);
    };
    
    ws.onmessage = (event) => {
      console.log('📨 FRONTEND DEBUG: WebSocket message received', event.data);
      const data = JSON.parse(event.data);
      console.log('📨 FRONTEND DEBUG: Parsed WebSocket data', data);
      handleWebSocketMessage(data);
    };
    
    ws.onclose = () => {
      console.log('🔌 FRONTEND DEBUG: WebSocket disconnected');
      setConnectionStatus('disconnected');
      setWsConnection(null);
    };
    
    ws.onerror = (error) => {
      console.error('❌ FRONTEND DEBUG: WebSocket error:', error);
      setConnectionStatus('error');
    };
  };

  const handleWebSocketMessage = (data) => {
    console.log('🔄 FRONTEND DEBUG: Handling WebSocket message', { type: data.type, message_type: data.message_type });
    
    if (data.type === 'message' || data.type === 'assistant_message') {
      console.log('💬 FRONTEND DEBUG: Processing message/assistant_message');
      if (data.message_type === 'assistant' || data.type === 'assistant_message') {
        const messageId = data.message_id || `msg_${Date.now()}_${Math.random()}`;
        
        // Check for duplicates
        if (processedMessageIds.has(messageId)) {
          console.log('⚠️ FRONTEND DEBUG: Skipping duplicate message', messageId);
          return;
        }
        
        const newMessage = {
          id: messageId,
          type: 'assistant',
          content: data.content,
          timestamp: data.timestamp || new Date().toISOString(),
          metadata: data.metadata
        };
        
        console.log('🤖 FRONTEND DEBUG: Adding assistant message', newMessage);
        setMessages(prev => [...prev, newMessage]);
        setProcessedMessageIds(prev => new Set([...prev, messageId]));
      }
      console.log('✅ FRONTEND DEBUG: Setting loading to false');
      setIsLoading(false);
    } else if (data.type === 'workflow_update') {
      console.log('🔧 FRONTEND DEBUG: Processing workflow_update');
      console.log('🔧 FRONTEND DEBUG: Raw data:', data);
      // Handle workflow update messages from backend
      const updateData = data.data || data;
      console.log('🔧 FRONTEND DEBUG: Update data:', updateData);
      console.log('🔧 FRONTEND DEBUG: Update data type:', updateData.type);
      
      // Check the type field in the nested data for assistant messages
      if (updateData.type === 'assistant') {
        const messageId = updateData.message_id || `workflow_msg_${Date.now()}_${Math.random()}`;

        // Check for duplicates
        if (processedMessageIds.has(messageId)) {
          console.log('⚠️ FRONTEND DEBUG: Skipping duplicate workflow message', messageId);
          return;
        }

        // AI responses during workflow execution
        const assistantMessage = {
          id: messageId,
          type: 'assistant',
          content: updateData.content,
          timestamp: updateData.timestamp || new Date().toISOString(),
          metadata: updateData.metadata
        };

        // Check for successful bill creation and extract NetSuite Bill ID
        const billCreationMatch = updateData.content?.match(/(?:Bill.*created|Bill.*successfully|NetSuite.*Bill.*ID)[:\s]*([A-Z0-9\-]+)|status.*success.*netsuite_bill_id[:\s]*([A-Z0-9\-]+)/i);
        if (billCreationMatch) {
          const billId = billCreationMatch[1] || billCreationMatch[2];
          console.log('✅ FRONTEND DEBUG: Bill creation detected! Bill ID:', billId);

          // Add success styling to message
          assistantMessage.billCreated = true;
          assistantMessage.billId = billId;

          // Auto-close modal after showing success for 3 seconds
          setTimeout(() => {
            console.log('🎉 Auto-closing modal after successful bill creation');
            onClose();
          }, 3000);
        }

        console.log('🤖 FRONTEND DEBUG: Adding assistant message from workflow_update', assistantMessage);
        setMessages(prev => [...prev, assistantMessage]);
        setProcessedMessageIds(prev => new Set([...prev, messageId]));
        setIsLoading(false);
        
      } else if (updateData.message_type === 'workflow_step') {
        // Step execution updates
        const stepMessage = {
          id: Date.now(),
          type: 'workflow',
          content: `🔄 ${updateData.content}`,
          timestamp: updateData.timestamp || new Date().toISOString(),
          metadata: updateData.metadata
        };
        setMessages(prev => [...prev, stepMessage]);
        setExecutionStatus('running');
        
      } else if (updateData.message_type === 'assistant') {
        // AI responses during workflow execution (legacy check)
        const assistantMessage = {
          id: updateData.message_id || Date.now(),
          type: 'assistant',
          content: updateData.content,
          timestamp: updateData.timestamp || new Date().toISOString(),
          metadata: updateData.metadata
        };
        setMessages(prev => [...prev, assistantMessage]);
        setIsLoading(false);
        
      } else if (updateData.message_type === 'tool_call') {
        // Tool call being made (before execution)
        const toolCallMessage = {
          id: updateData.tool_call_id || Date.now(),
          type: 'tool_call',
          content: updateData.content,
          timestamp: updateData.timestamp || new Date().toISOString(),
          metadata: updateData.metadata,
          toolName: updateData.tool_name,
          toolArguments: updateData.tool_arguments,
          status: 'calling'
        };
        setMessages(prev => [...prev, toolCallMessage]);
        
      } else if (updateData.message_type === 'tool_result') {
        // Tool execution results
        const toolResultMessage = {
          id: Date.now(),
          type: 'tool_result',
          content: updateData.content,
          timestamp: updateData.timestamp || new Date().toISOString(),
          metadata: updateData.metadata,
          toolName: updateData.tool_name,
          toolResult: updateData.tool_result,
          success: updateData.success,
          errorMessage: updateData.error_message,
          executionTime: updateData.execution_time,
          toolCallId: updateData.tool_call_id
        };

        // Check for successful bill creation in tool result
        if (updateData.success && updateData.tool_name === 'create_bill_from_purchase_order') {
          const toolResultStr = JSON.stringify(updateData.tool_result || {});
          const billIdMatch = toolResultStr.match(/netsuite_bill_id[:\s]*["']?([A-Z0-9\-]+)["']?/i);
          if (billIdMatch) {
            const billId = billIdMatch[1];
            console.log('✅ FRONTEND DEBUG: Bill creation detected in tool result! Bill ID:', billId);

            toolResultMessage.billCreated = true;
            toolResultMessage.billId = billId;

            // Auto-close modal after showing success for 3 seconds
            setTimeout(() => {
              console.log('🎉 Auto-closing modal after successful bill creation from tool result');
              onClose();
            }, 3000);
          }
        }

        setMessages(prev => [...prev, toolResultMessage]);
        
      } else if (updateData.response_type === 'final_response') {
        // Workflow completion
        setExecutionStatus('completed');
        setIsLoading(false);
      }
      
    } else if (data.type === 'workflow_step') {
      // Handle legacy workflow step updates (backwards compatibility)
      const stepMessage = {
        id: Date.now(),
        type: 'workflow',
        content: `🔄 ${data.step_name}: ${data.content}`,
        timestamp: data.timestamp,
        metadata: data.metadata
      };
      setMessages(prev => [...prev, stepMessage]);
      
      if (data.status === 'running') {
        setExecutionStatus('running');
      } else if (data.status === 'completed') {
        setExecutionStatus('completed');
      } else if (data.status === 'error') {
        setExecutionStatus('error');
      }
    } else if (data.type === 'error') {
      console.error('Workflow chat error:', data.error);
      setIsLoading(false);
      setExecutionStatus('error');
      setMessages(prev => [...prev, {
        id: Date.now(),
        type: 'error',
        content: `❌ Error: ${data.error}`,
        timestamp: new Date().toISOString()
      }]);
    }
  };

  const sendMessage = async () => {
    console.log('🚀 FRONTEND DEBUG: sendMessage called', { inputMessage, sessionId, workflowId: workflow?.id });
    
    if (!inputMessage.trim() || !sessionId) {
      console.log('❌ FRONTEND DEBUG: Missing input or sessionId', { inputMessage: inputMessage.trim(), sessionId });
      return;
    }

    console.log('📤 FRONTEND DEBUG: Starting workflow execution...');
    setIsLoading(true);
    setExecutionStatus('running');
    
    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: inputMessage,
      timestamp: new Date().toISOString()
    };
    
    setMessages(prev => [...prev, userMessage]);
    const messageToSend = inputMessage;
    setInputMessage('');

    try {
      console.log('🌐 FRONTEND DEBUG: Sending API request...', {
        url: `${API_BASE_URL}/api/chat/workflows/execute`,
        sessionId,
        workflowId: workflow.id,
        message: messageToSend
      });

      // Send message with workflow execution
      const response = await fetch(`${API_BASE_URL}/api/chat/workflows/execute`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          session_id: sessionId,
          workflow_id: String(workflow.id),
          user_message: messageToSend,
          provider: 'openai',
          model: 'gpt-4o'
        })
      });

      if (!response.ok) {
        console.log('❌ FRONTEND DEBUG: API response not ok', { status: response.status, statusText: response.statusText });
        throw new Error(`Failed to execute workflow: ${response.status} ${response.statusText}`);
      }
      
      const result = await response.json();
      console.log('✅ FRONTEND DEBUG: API response received', result);
      
    } catch (error) {
      console.error('❌ FRONTEND DEBUG: Failed to send message:', error);
      setMessages(prev => [...prev, {
        id: Date.now(),
        type: 'error',
        content: `❌ Error: ${error.message}`,
        timestamp: new Date().toISOString()
      }]);
      setIsLoading(false);
      setExecutionStatus('error');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const getMessageIcon = (type) => {
    switch (type) {
      case 'user': return faUser;
      case 'assistant': return faRobot;
      case 'workflow': return faProjectDiagram;
      case 'system': return faCircle;
      case 'error': return faTimes;
      default: return faCircle;
    }
  };

  const getMessageColor = (type) => {
    switch (type) {
      case 'user': return 'bg-blue-600 text-white';
      case 'assistant': return 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white border border-gray-200 dark:border-gray-700';
      case 'workflow': return 'bg-purple-50 dark:bg-purple-900/20 text-purple-800 dark:text-purple-200 border border-purple-200 dark:border-purple-800';
      case 'system': return 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200';
      case 'error': return 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-400 border border-red-200 dark:border-red-800';
      default: return 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200';
    }
  };

  const getStatusIcon = () => {
    switch (executionStatus) {
      case 'running': return faSpinner;
      case 'completed': return faStop;
      case 'error': return faTimes;
      case 'ready': return faPlay;
      default: return faCircle;
    }
  };

  const getStatusColor = () => {
    switch (executionStatus) {
      case 'running': return 'text-blue-500';
      case 'completed': return 'text-green-500';
      case 'error': return 'text-red-500';
      case 'ready': return 'text-purple-500';
      default: return 'text-gray-500';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow-2xl w-full max-w-4xl h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-purple-50 to-blue-50 dark:from-gray-800 dark:to-gray-800">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded-lg">
              <FontAwesomeIcon icon={faProjectDiagram} className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Workflow Chat: {workflow?.name}
              </h2>
              <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                <FontAwesomeIcon 
                  icon={getStatusIcon()} 
                  className={`${getStatusColor()} ${executionStatus === 'running' ? 'animate-spin' : ''}`} 
                />
                <span className="capitalize">{executionStatus}</span>
                {connectionStatus === 'connected' && (
                  <>
                    <span className="text-gray-400">•</span>
                    <div className="flex items-center space-x-1">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <span>Connected</span>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          >
            <FontAwesomeIcon icon={faTimes} className="w-5 h-5" />
          </button>
        </div>

        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50 dark:bg-gray-900">
          {messages.length === 0 && (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <FontAwesomeIcon icon={faProjectDiagram} className="text-white text-2xl w-8 h-8" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Workflow Ready!
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Send a message to start executing "{workflow?.name}"
              </p>
            </div>
          )}
          
          {messages.map((message) => (
            <div key={message.id} className={`flex items-start space-x-3 ${
              message.type === 'user' ? 'justify-end' : 'justify-start'
            }`}>
              {message.type !== 'user' && (
                <div className="flex-shrink-0 p-2 bg-gray-100 dark:bg-gray-700 rounded-full">
                  <FontAwesomeIcon icon={getMessageIcon(message.type)} className="w-4 h-4" />
                </div>
              )}
              
              {/* Tool Call UI - Claude Desktop Style */}
              {message.type === 'tool_call' && (
                <div className="max-w-3xl bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-3">
                  <div className="flex items-center space-x-2 mb-2">
                    <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 text-orange-500 animate-spin" />
                    <span className="font-medium text-orange-700 dark:text-orange-300">
                      Calling {message.toolName}
                    </span>
                  </div>
                  
                  {message.toolArguments && (
                    <div className="mt-2">
                      <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Input:</div>
                      <pre className="text-xs bg-gray-100 dark:bg-gray-800 p-2 rounded overflow-x-auto">
                        {JSON.stringify(message.toolArguments, null, 2)}
                      </pre>
                    </div>
                  )}
                  
                  <div className="flex items-center justify-between mt-2 text-xs opacity-70">
                    <span>{new Date(message.timestamp).toLocaleTimeString()}</span>
                    <span className="text-orange-500">Executing...</span>
                  </div>
                </div>
              )}
              
              {/* Tool Result UI - Claude Desktop Style */}
              {message.type === 'tool_result' && (
                <div className={`max-w-3xl ${message.success ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800' : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'} border rounded-lg p-3`}>
                  <div className="flex items-center space-x-2 mb-2">
                    <FontAwesomeIcon 
                      icon={message.success ? faCheck : faTimes} 
                      className={`w-4 h-4 ${message.success ? 'text-green-500' : 'text-red-500'}`} 
                    />
                    <span className={`font-medium ${message.success ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'}`}>
                      {message.toolName} {message.success ? 'completed' : 'failed'}
                    </span>
                    {message.executionTime && (
                      <span className="text-xs text-gray-500">
                        ({message.executionTime.toFixed(2)}s)
                      </span>
                    )}
                  </div>
                  
                  {message.success && message.toolResult && (
                    <div className="mt-2">
                      <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Output:</div>
                      <pre className="text-xs bg-gray-100 dark:bg-gray-800 p-2 rounded overflow-x-auto max-h-40 overflow-y-auto">
                        {typeof message.toolResult === 'object' 
                          ? JSON.stringify(message.toolResult, null, 2)
                          : message.toolResult}
                      </pre>
                    </div>
                  )}
                  
                  {!message.success && message.errorMessage && (
                    <div className="mt-2">
                      <div className="text-sm text-red-600 dark:text-red-400 mb-1">Error:</div>
                      <div className="text-xs bg-red-100 dark:bg-red-800 p-2 rounded">
                        {message.errorMessage}
                      </div>
                    </div>
                  )}
                  
                  <div className="flex items-center justify-between mt-2 text-xs opacity-70">
                    <span>{new Date(message.timestamp).toLocaleTimeString()}</span>
                    <span className={message.success ? 'text-green-500' : 'text-red-500'}>
                      {message.success ? 'Success' : 'Failed'}
                    </span>
                  </div>
                </div>
              )}
              
              {/* Regular Message UI */}
              {!['tool_call', 'tool_result'].includes(message.type) && (
                <div className={`max-w-3xl ${message.billCreated ?
                  'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800' :
                  getMessageColor(message.type)} rounded-lg p-3`}>

                  {/* Special success header for bill creation */}
                  {message.billCreated && (
                    <div className="flex items-center space-x-2 mb-3 pb-2 border-b border-green-200 dark:border-green-700">
                      <FontAwesomeIcon icon={faCheck} className="w-5 h-5 text-green-600 dark:text-green-400" />
                      <span className="font-bold text-green-800 dark:text-green-200 text-lg">
                        🎉 Bill Created Successfully!
                      </span>
                    </div>
                  )}

                  {/* Bill ID highlight */}
                  {message.billCreated && message.billId && (
                    <div className="bg-green-100 dark:bg-green-800 rounded-lg p-3 mb-3">
                      <div className="text-sm text-green-700 dark:text-green-300 mb-1">NetSuite Bill ID:</div>
                      <div className="font-mono text-lg font-bold text-green-900 dark:text-green-100">
                        {message.billId}
                      </div>
                    </div>
                  )}

                  <div className="whitespace-pre-wrap">{message.content}</div>
                  <div className="flex items-center justify-between mt-2 text-xs opacity-70">
                    <span>{new Date(message.timestamp).toLocaleTimeString()}</span>
                    {message.metadata?.tokens_used && (
                      <span>{message.metadata.tokens_used} tokens</span>
                    )}
                    {message.billCreated && (
                      <span className="text-green-600 dark:text-green-400 font-medium">
                        ✨ Auto-closing in 3s
                      </span>
                    )}
                  </div>
                </div>
              )}
              
              {message.type === 'user' && (
                <div className="flex-shrink-0 p-2 bg-blue-100 dark:bg-blue-900 rounded-full">
                  <FontAwesomeIcon icon={getMessageIcon(message.type)} className="w-4 h-4" />
                </div>
              )}
            </div>
          ))}
          
          {isLoading && (
            <div className="flex items-center space-x-2 text-gray-500 dark:text-gray-400">
              <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
              <span>Executing workflow...</span>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
          <div className="flex space-x-2">
            <textarea
              ref={inputRef}
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Type your message to execute the workflow..."
              disabled={isLoading || !sessionId}
              className="flex-1 p-3 border border-gray-300 dark:border-gray-600 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              rows={2}
            />
            <button
              onClick={sendMessage}
              disabled={!inputMessage.trim() || isLoading || !sessionId}
              className="p-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <FontAwesomeIcon icon={faPaperPlane} className="w-5 h-5" />
            </button>
          </div>
          <div className="flex justify-between items-center mt-2 text-xs text-gray-500 dark:text-gray-400">
            <span>Press Enter to send, Shift+Enter for new line</span>
            {sessionId && <span>Session: {sessionId.substring(0, 8)}...</span>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkflowChatDialog;